import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@/components/video/Breadcrumb";
import VideoCard from "@/components/video/VideoCard";
import { videos } from "@/mocks/videos";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

type PlayState = "idle" | "loading" | "playing";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const video = useMemo(() => videos.find((v) => v.id === id), [id]);
  const [playState, setPlayState] = useState<PlayState>("idle");

  useEffect(() => {
    setPlayState("idle");
  }, [id]);

  useDocumentTitle(
    video ? `${video.title} | 黄瓜视频App` : "视频详情 | 黄瓜视频App",
    video
      ? `${video.title}（${video.year} · ${video.region} · ${video.duration}）：${video.description}`
      : "黄瓜视频App视频详情页，查看视频简介、播放演示与相关推荐。",
    video ? `${video.title},黄瓜视频App,${video.type},高清视频` : "黄瓜视频App,视频详情,高清视频",
  );

  useEffect(() => {
    if (playState !== "loading") return;
    const timer = setTimeout(() => setPlayState("playing"), 1500);
    return () => clearTimeout(timer);
  }, [playState]);

  const related = useMemo(() => {
    if (!video) return [];
    return videos.filter((v) => v.type === video.type && v.id !== video.id).slice(0, 6);
  }, [video]);

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 pt-28 md:pt-36 pb-24 text-center">
        <h1 className="text-2xl font-heading font-bold text-foreground-950">未找到该视频</h1>
        <p className="mt-3 text-foreground-500">视频可能已下架或链接有误，返回首页继续浏览吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full pt-20 md:pt-24">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-10 border-b border-background-200/60">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: `${video.type}`, to: `/category/${video.type}` }, { label: video.title }]}
        />
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="lg:col-span-3">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-background-200/60 bg-background-950">
              <img
                src={video.cover}
                alt={`${video.title} 视频封面`}
                title={`${video.title} - 黄瓜视频App高清视频`}
                className={`w-full h-full object-cover object-top transition-all duration-500 ${
                  playState === "playing" ? "opacity-30 blur-sm" : "opacity-100"
                }`}
              />

              {playState === "idle" && (
                <button
                  type="button"
                  onClick={() => setPlayState("loading")}
                  aria-label={`播放 ${video.title}`}
                  className="absolute inset-0 flex items-center justify-center bg-background-950/40 hover:bg-background-950/30 transition-colors cursor-pointer group"
                >
                  <span className="w-20 h-20 rounded-full bg-primary-500 text-background-950 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-4xl" />
                  </span>
                </button>
              )}

              {playState === "loading" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background-950/60">
                  <span className="w-12 h-12 rounded-full border-4 border-foreground-700/40 border-t-primary-500 animate-spin" />
                  <p className="text-sm text-foreground-300">正在加载视频演示…</p>
                </div>
              )}

              {playState === "playing" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                  <div className="flex items-end gap-1.5 h-16">
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                      <span
                        key={i}
                        className="w-2 rounded-full bg-primary-500 animate-pulse-bar"
                        style={{ height: "100%", animationDelay: `${i * 0.12}s` }}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-200">播放演示中（前端模拟，非真实视频）</p>
                  <button
                    type="button"
                    onClick={() => setPlayState("idle")}
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-foreground-600/60 text-foreground-200 text-sm hover:border-primary-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-stop-circle-line w-4 h-4 flex items-center justify-center" />
                    停止演示
                  </button>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-information-line w-4 h-4 flex items-center justify-center text-accent-400" />
              提示：本页面播放按钮仅用于功能演示，不会加载真实视频流。
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/download"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line w-5 h-5 flex items-center justify-center" />
                下载App观看完整版
              </Link>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300/70 hover:border-primary-500 text-foreground-800 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-go-back-line w-5 h-5 flex items-center justify-center" />
                返回上一页
              </button>
              <Link
                to={`/category/${video.type}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300/70 hover:border-primary-500 text-foreground-800 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-list-check-2 w-5 h-5 flex items-center justify-center" />
                返回{video.type}列表
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="p-6 rounded-lg bg-background-100 border border-background-200/60">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-label bg-primary-500 text-background-950">
                {video.type}
              </span>
              <h1 className="mt-3 text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight">
                {video.title}
              </h1>
              <p className="mt-4 text-sm text-foreground-400 leading-relaxed">{video.description}</p>

              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex items-center justify-between py-2.5 border-b border-background-200/60">
                  <dt className="text-foreground-500">分类</dt>
                  <dd className="text-foreground-900">{video.type}</dd>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-background-200/60">
                  <dt className="text-foreground-500">发布时间</dt>
                  <dd className="text-foreground-900">{video.publishDate}</dd>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-background-200/60">
                  <dt className="text-foreground-500">时长</dt>
                  <dd className="text-foreground-900">{video.duration}</dd>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-background-200/60">
                  <dt className="text-foreground-500">年份</dt>
                  <dd className="text-foreground-900">{video.year}</dd>
                </div>
                <div className="flex items-center justify-between py-2.5">
                  <dt className="text-foreground-500">地区</dt>
                  <dd className="text-foreground-900">{video.region}</dd>
                </div>
              </dl>

              <div className="mt-5 flex items-center gap-2 text-sm text-accent-400">
                <i className="ri-fire-line w-4 h-4 flex items-center justify-center" />
                热度 {video.hot}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-sm font-label text-accent-400 tracking-wide">Related</p>
              <h2 className="mt-1 text-2xl md:text-3xl font-heading font-bold text-foreground-950">相关推荐</h2>
            </div>
            <Link
              to={`/category/${video.type}`}
              className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-400 hover:gap-2 transition-all cursor-pointer"
            >
              查看全部{video.type}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <VideoCard key={item.id} video={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}